import { createHttpClient } from '@adapters/HttpClient';
import { useQueryClient, useMutation, InfiniteData } from '@tanstack/react-query';
import { useToast } from '@shared/hooks/use-toast';
import { apiClientWH } from '@shared/services';
import { useTranslation } from 'react-i18next';
import { RolesApiAdapter } from '../adapters/RolesApiAdapter';
import { RolesModel } from '../models/Roles';
import { Page } from '../services/Service';

interface UpdateRoleVariables {
  roleId: number | string;
  name: string;
  permissions: string[];
}

/**
 * Hook para actualizar un rol existente
 * Actualiza solo la fila específica en el cache sin recargar toda la lista
 */
export const useUpdateRole = () => {
  const { t } = useTranslation('rolesModule');
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const httpClient = createHttpClient(apiClientWH);
  const apiAdapter = new RolesApiAdapter(httpClient);

  return useMutation({
    mutationFn: async ({ roleId, name, permissions }: UpdateRoleVariables) => {
      console.log('🔍 useUpdateRole - Updating role:', { roleId, name, permissions });
      
      const updatedRole = await apiAdapter.updateRole(roleId, name, permissions);
      
      console.log('🔍 useUpdateRole - Role updated successfully:', updatedRole);
      
      return { roleId: Number(roleId), updatedRole };
    },
    onSuccess: ({ roleId, updatedRole }) => {
      // 🎯 Actualizar solo el rol específico en el cache de la infinite query
      queryClient.setQueriesData<InfiniteData<Page>>(
        { queryKey: ['roles'] },
        (oldData) => {
          if (!oldData) return oldData;

          return {
            ...oldData,
            pages: oldData.pages.map((page) => ({
              ...page,
              roles: page.roles.map((role) =>
                role.id === roleId ? updatedRole : role
              ),
            })),
          };
        }
      );

      // 🎯 Mostrar toast de éxito
      toast({
        title: t('toast.editSuccess', 'Rol actualizado correctamente.'),
      });
    },
    onError: (error: any) => {
      console.log('🔍 useUpdateRole - Error updating role:', error);

      const apiMsg =
        error?.response?.data?.message ||
        error?.message ||
        t('toast.editError', 'No se pudo actualizar el rol.');

      toast({
        title: t('toast.editError', 'No se pudo actualizar el rol.'),
        description: apiMsg,
        variant: 'destructive',
      });
    },
  });
};

